import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { dailyTaskService } from '@/services/dailyTaskService';

interface PendingTask {
  id: string;
  type: 'social_media' | 'journal_entry';
  title: string;
  description: string;
  route: string;
}

export const useCashpurrNotifications = () => {
  const { user } = useAuth();
  const [currentTask, setCurrentTask] = useState<PendingTask | null>(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    checkForPendingTasks();

    // Check every 5 minutes for pending tasks (reduced from 10 minutes for testing)
    const interval = setInterval(() => {
      checkForPendingTasks();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user?.id]);

  const checkForPendingTasks = async () => {
    if (!user?.id || showPopup) return; // Don't show multiple popups

    try {
      console.log('Checking for pending tasks for user:', user.id);
      
      // Get today's daily tasks
      const dailyTasks = await dailyTaskService.getTodaysDailyTasks(user.id);
      console.log('Daily tasks retrieved:', dailyTasks);
      
      const pendingTasks = dailyTasks.filter(task => !task.completed);
      console.log('Pending tasks found:', pendingTasks.length);

      if (pendingTasks.length === 0) {
        console.log('No pending tasks found');
        return;
      }

      // Check if we already showed a notification for this task today
      const today = new Date().toISOString().split('T')[0];
      const { data: recentNotifications } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'popup_closed')
        .gte('created_at', today + 'T00:00:00')
        .order('created_at', { ascending: false });

      // Find a task that hasn't been dismissed today
      const taskToShow = pendingTasks.find(task => {
        return !recentNotifications?.some(notif => {
          const context = notif.context as any;
          return context?.task_id === task.id;
        });
      });

      if (!taskToShow) return;

      // Prioritize high-importance tasks (social media over journal)
      const prioritizedTask = pendingTasks.find(task => task.task_type === 'social_media') || taskToShow;

      const pendingTaskForPopup: PendingTask = {
        id: prioritizedTask.id,
        type: prioritizedTask.task_type,
        title: prioritizedTask.title,
        description: prioritizedTask.description,
        route: prioritizedTask.task_type === 'social_media' ? '/app/social-tracker' : '/app/founder-journal'
      };

      setCurrentTask(pendingTaskForPopup);
      
      // Show popup after a delay to avoid overwhelming on page load
      setTimeout(() => {
        console.log('Showing Cashpurr notification popup for task:', prioritizedTask.title);
        setShowPopup(true);
      }, 10000); // Reduced to 10 seconds for testing

    } catch (error) {
      console.error('Error checking for pending tasks:', error);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentTask(null);
  };

  return {
    showPopup,
    currentTask,
    handleClosePopup
  };
};